import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { FiPlus, FiZap, FiEdit2, FiTrash2, FiEye, FiEyeOff, FiRefreshCw, FiClock } from 'react-icons/fi';

export default function BlogManager() {
  const [blogs, setBlogs] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [showTopics, setShowTopics] = useState(false);
  const [showCuratedTopics, setShowCuratedTopics] = useState(false);
  const [topics, setTopics] = useState([]);
  const [curatedTopics, setCuratedTopics] = useState([]);
  const [curatedStats, setCuratedStats] = useState({});
  const [generating, setGenerating] = useState(false);
  const [topicLoading, setTopicLoading] = useState(false);
  const [curatedLoading, setCuratedLoading] = useState(false);
  const [autoGenStatus, setAutoGenStatus] = useState(null);
  const [editBlog, setEditBlog] = useState(null);
  const [form, setForm] = useState({ title: '', content: '', tags: '', metaTitle: '', metaDescription: '', isPublished: false });

  useEffect(() => {
    fetchBlogs();
    fetchAutoStatus();
  }, []);

  const fetchBlogs = async () => {
    try {
      const { data } = await api.get('/blog/admin/all');
      setBlogs(data);
    } catch {
      toast.error('Failed to fetch blogs');
    }
  };

  const fetchAutoStatus = async () => {
    try {
      const { data } = await api.get('/blog/ai/auto-status');
      setAutoGenStatus(data);
    } catch {
      // Non-fatal — panel just shows defaults
    }
  };

  const handleToggleAuto = async () => {
    const next = !autoGenStatus?.enabled;
    try {
      const { data } = await api.put('/blog/ai/auto-config', { enabled: next });
      setAutoGenStatus(data);
      toast.success(next ? 'Daily auto blog generation resumed' : 'Daily auto blog generation paused');
    } catch {
      toast.error('Failed to update setting');
    }
  };

  const handleChangePostsPerDay = async (postsPerDay) => {
    try {
      const { data } = await api.put('/blog/ai/auto-config', { postsPerDay });
      setAutoGenStatus(data);
    } catch {
      toast.error('Failed to update posts per day');
    }
  };

  const handleAITopics = async () => {
    setTopicLoading(true);
    try {
      const { data } = await api.post('/blog/ai/topics', { niche: 'email productivity and AI writing tools' });
      setTopics(data.topics);
      setShowTopics(true);
    } catch {
      toast.error('Failed to generate topics');
    } finally {
      setTopicLoading(false);
    }
  };

  const handleCuratedTopics = async () => {
    setCuratedLoading(true);
    try {
      const { data } = await api.get('/blog/ai/curated-topics?count=15');
      setCuratedTopics(data.topics);
      setCuratedStats({ totalAvailable: data.totalAvailable, totalUsed: data.totalUsed, totalTopics: data.totalTopics });
      setShowCuratedTopics(true);
    } catch {
      toast.error('Failed to load curated topics');
    } finally {
      setCuratedLoading(false);
    }
  };

  const handleAutoGenerate = async () => {
    const count = autoGenStatus?.postsPerDay || 3;
    if (!window.confirm(`Generate ${count} new blog post(s) as drafts? This may take a few minutes.`)) return;
    try {
      await api.post('/blog/ai/auto-generate', { count });
      toast.success('Auto-generation started! Posts will appear as drafts in a few minutes.');
      // Poll status
      const pollInterval = setInterval(async () => {
        try {
          const { data } = await api.get('/blog/ai/auto-status');
          setAutoGenStatus(data);
          if (!data.isGenerating) {
            clearInterval(pollInterval);
            fetchBlogs();
            if (data.lastResult.success > 0) {
              toast.success(`${data.lastResult.success} blog post(s) generated as drafts!`);
            }
          }
        } catch { clearInterval(pollInterval); }
      }, 5000);
    } catch {
      toast.error('Failed to start auto-generation');
    }
  };

  const handleGeneratePost = async (title, keyword) => {
    setGenerating(true);
    setShowTopics(false);
    toast.loading('Generating blog post...', { id: 'gen' });
    try {
      await api.post('/blog/ai/generate', { title, keyword });
      toast.success('Blog post generated!', { id: 'gen' });
      fetchBlogs();
    } catch {
      toast.error('Failed to generate post', { id: 'gen' });
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) };
      if (editBlog) {
        await api.put(`/blog/${editBlog._id}`, payload);
        toast.success('Blog updated');
      } else {
        await api.post('/blog', payload);
        toast.success('Blog created');
      }
      setShowEditor(false);
      setEditBlog(null);
      setForm({ title: '', content: '', tags: '', metaTitle: '', metaDescription: '', isPublished: false });
      fetchBlogs();
    } catch {
      toast.error('Failed to save blog');
    }
  };

  const handleEdit = (blog) => {
    setEditBlog(blog);
    setForm({
      title: blog.title,
      content: blog.content,
      tags: (blog.tags || []).join(', '),
      metaTitle: blog.metaTitle || '',
      metaDescription: blog.metaDescription || '',
      isPublished: blog.isPublished
    });
    setShowEditor(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog post?')) return;
    try {
      await api.delete(`/blog/${id}`);
      toast.success('Blog deleted');
      fetchBlogs();
    } catch {
      toast.error('Failed to delete blog');
    }
  };

  const handleTogglePublish = async (blog) => {
    try {
      await api.put(`/blog/${blog._id}`, { isPublished: !blog.isPublished });
      toast.success(blog.isPublished ? 'Unpublished' : 'Published');
      fetchBlogs();
    } catch {
      toast.error('Failed to update blog');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 28, fontWeight: 700 }}>Blog Manager</h1>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={handleAutoGenerate} disabled={autoGenStatus?.isGenerating} style={styles.autoBtn}>
            <FiRefreshCw size={16} className={autoGenStatus?.isGenerating ? 'spin' : ''} /> {autoGenStatus?.isGenerating ? 'Generating...' : `Auto-Generate ${autoGenStatus?.postsPerDay || 3} Drafts`}
          </button>
          <button onClick={handleCuratedTopics} disabled={curatedLoading} style={styles.curatedBtn}>
            <FiClock size={16} /> {curatedLoading ? 'Loading...' : 'SEO/GEO Topics'}
          </button>
          <button onClick={handleAITopics} disabled={topicLoading} style={styles.aiBtn}>
            <FiZap size={16} /> {topicLoading ? 'Generating...' : 'AI Topic Suggestions'}
          </button>
          <button onClick={() => { setEditBlog(null); setForm({ title: '', content: '', tags: '', metaTitle: '', metaDescription: '', isPublished: false }); setShowEditor(true); }} style={styles.createBtn}>
            <FiPlus size={16} /> Create Post
          </button>
        </div>
      </div>

      {/* Daily Auto Blog Generation Panel */}
      <div style={styles.autoPanel}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h3 style={{ fontSize: 17, fontWeight: 600 }}>Daily Auto Blog Generation</h3>
              <span style={{
                fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 6, textTransform: 'uppercase',
                background: autoGenStatus?.enabled ? 'rgba(34,197,94,0.15)' : 'rgba(148,163,184,0.15)',
                color: autoGenStatus?.enabled ? 'var(--success)' : 'var(--text-muted)'
              }}>
                {autoGenStatus?.enabled ? 'On' : 'Off'}
              </span>
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 6 }}>
              Automatically generates SEO-optimized blog drafts every day at 6:00 AM server time.
            </p>
          </div>

          {/* Toggle switch */}
          <button
            onClick={handleToggleAuto}
            role="switch"
            aria-checked={!!autoGenStatus?.enabled}
            title={autoGenStatus?.enabled ? 'Pause daily generation' : 'Resume daily generation'}
            style={{
              position: 'relative', width: 52, height: 28, borderRadius: 999, border: 'none', cursor: 'pointer',
              flexShrink: 0, transition: 'background 0.2s',
              background: autoGenStatus?.enabled ? 'var(--success, #22c55e)' : 'var(--border)'
            }}
          >
            <span style={{
              position: 'absolute', top: 3, left: autoGenStatus?.enabled ? 27 : 3, width: 22, height: 22,
              borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
            }} />
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, flexWrap: 'wrap', opacity: autoGenStatus?.enabled ? 1 : 0.55 }}>
          <label style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Posts per day</label>
          <select
            value={autoGenStatus?.postsPerDay || 3}
            onChange={e => handleChangePostsPerDay(parseInt(e.target.value))}
            disabled={!autoGenStatus?.enabled}
            style={{ padding: '8px 12px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, cursor: 'pointer' }}
          >
            {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} {n === 1 ? 'post' : 'posts'}</option>)}
          </select>
          <button onClick={handleAutoGenerate} disabled={autoGenStatus?.isGenerating} style={styles.runNowBtn}>
            <FiRefreshCw size={14} className={autoGenStatus?.isGenerating ? 'spin' : ''} /> Run Now
          </button>
        </div>
      </div>

      {/* AI Topics Modal */}
      {showTopics && (
        <div style={styles.modalOverlay} onClick={() => setShowTopics(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 16 }}>AI Topic Suggestions</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>Click any topic to generate a full 2000+ word SEO-optimized article</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
              {topics.map((t, i) => (
                <button
                  key={i}
                  onClick={() => handleGeneratePost(t.title, t.keyword)}
                  disabled={generating}
                  style={styles.topicItem}
                >
                  <strong>{t.title}</strong>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Keyword: {t.keyword}</span>
                  <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t.description}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Curated SEO/GEO Topics Modal */}
      {showCuratedTopics && (
        <div style={styles.modalOverlay} onClick={() => setShowCuratedTopics(false)}>
          <div style={{ ...styles.modal, width: 600 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 8 }}>SEO/GEO/AI-Optimized Topics</h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 4 }}>
              Curated topics optimized for Google SEO, AI search (GEO), and AI visibility. Duplicates auto-excluded.
            </p>
            <p style={{ color: 'var(--accent)', fontSize: 12, marginBottom: 16 }}>
              {curatedStats.totalAvailable} available / {curatedStats.totalUsed} used / {curatedStats.totalTopics} total
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 450, overflowY: 'auto' }}>
              {curatedTopics.map((t, i) => (
                <button
                  key={i}
                  onClick={() => { setShowCuratedTopics(false); handleGeneratePost(t.title, t.keyword); }}
                  disabled={generating}
                  style={styles.topicItem}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <strong>{t.title}</strong>
                    <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: 'rgba(99,102,241,0.15)', color: 'var(--accent)', whiteSpace: 'nowrap', marginLeft: 8 }}>{t.category}</span>
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Keyword: {t.keyword} | Intent: {t.searchIntent}</span>
                </button>
              ))}
              {curatedTopics.length === 0 && (
                <p style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 20 }}>All topics have been used! Time to add more.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Auto-gen Status Banner */}
      {autoGenStatus?.isGenerating && (
        <div style={{ padding: '12px 16px', background: 'rgba(99,102,241,0.1)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 10, marginBottom: 16, fontSize: 13, color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <FiRefreshCw size={14} className="spin" /> Auto-generating blog posts... {autoGenStatus.lastResult?.success || 0} completed so far
        </div>
      )}

      {/* Editor Modal */}
      {showEditor && (
        <div style={styles.modalOverlay} onClick={() => setShowEditor(false)}>
          <div style={{ ...styles.modal, width: 700 }} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginBottom: 16 }}>{editBlog ? 'Edit Post' : 'Create Post'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <input placeholder="Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} style={styles.modalInput} />
              <textarea placeholder="Content (HTML or Markdown)" value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} rows={12} style={{ ...styles.modalInput, resize: 'vertical' }} />
              <input placeholder="Tags (comma separated)" value={form.tags} onChange={e => setForm({ ...form, tags: e.target.value })} style={styles.modalInput} />
              <input placeholder="SEO Meta Title" value={form.metaTitle} onChange={e => setForm({ ...form, metaTitle: e.target.value })} style={styles.modalInput} />
              <input placeholder="SEO Meta Description" value={form.metaDescription} onChange={e => setForm({ ...form, metaDescription: e.target.value })} style={styles.modalInput} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.isPublished} onChange={e => setForm({ ...form, isPublished: e.target.checked })} />
                <span>Publish immediately</span>
              </label>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setShowEditor(false)} style={styles.cancelBtn}>Cancel</button>
                <button onClick={handleSave} style={styles.saveBtn}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Blog List */}
      <div style={styles.table}>
        <div style={styles.tableHeader}>
          <span style={{ flex: 3 }}>Title</span>
          <span style={{ flex: 1 }}>Status</span>
          <span style={{ flex: 1 }}>SEO</span>
          <span style={{ flex: 1 }}>Views</span>
          <span style={{ flex: 1 }}>Type</span>
          <span style={{ flex: 1, textAlign: 'right' }}>Actions</span>
        </div>
        {blogs.map(blog => (
          <div key={blog._id} style={styles.tableRow}>
            <span style={{ flex: 3, fontWeight: 500 }}>{blog.title}</span>
            <span style={{ flex: 1 }}>
              <span style={{ color: blog.isPublished ? 'var(--success)' : 'var(--warning)' }}>
                {blog.isPublished ? 'Published' : 'Draft'}
              </span>
            </span>
            <span style={{ flex: 1 }}>
              <span style={{ padding: '2px 8px', borderRadius: 8, fontSize: 12, background: blog.seoScore >= 80 ? 'rgba(34,197,94,0.15)' : 'rgba(245,158,11,0.15)', color: blog.seoScore >= 80 ? 'var(--success)' : 'var(--warning)' }}>
                {blog.seoScore || 0}
              </span>
            </span>
            <span style={{ flex: 1, color: 'var(--text-muted)' }}>{blog.views}</span>
            <span style={{ flex: 1 }}>
              <span style={{ fontSize: 12, color: blog.isAIGenerated ? 'var(--accent)' : 'var(--text-muted)' }}>
                {blog.isAIGenerated ? 'AI' : 'Manual'}
              </span>
            </span>
            <span style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 4 }}>
              <button onClick={() => handleTogglePublish(blog)} style={styles.actionBtn} title={blog.isPublished ? 'Unpublish' : 'Publish'}>
                {blog.isPublished ? <FiEyeOff size={14} /> : <FiEye size={14} />}
              </button>
              <button onClick={() => handleEdit(blog)} style={styles.actionBtn}><FiEdit2 size={14} /></button>
              <button onClick={() => handleDelete(blog._id)} style={{ ...styles.actionBtn, color: 'var(--danger)' }}><FiTrash2 size={14} /></button>
            </span>
          </div>
        ))}
        {blogs.length === 0 && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            No blog posts yet. Create one or use AI to generate!
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  autoBtn: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: 'linear-gradient(135deg, #10b981, #059669)', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  autoPanel: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, padding: 20, marginBottom: 20 },
  runNowBtn: { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 13, fontWeight: 500, cursor: 'pointer' },
  curatedBtn: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  aiBtn: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  createBtn: { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px', background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 14, cursor: 'pointer' },
  table: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' },
  tableHeader: { display: 'flex', padding: '12px 16px', fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', borderBottom: '1px solid var(--border)', textTransform: 'uppercase' },
  tableRow: { display: 'flex', padding: '12px 16px', fontSize: 14, color: 'var(--text-primary)', borderBottom: '1px solid var(--border)', alignItems: 'center' },
  actionBtn: { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 6, borderRadius: 4, display: 'flex' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 16, padding: 28, width: 500, maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto' },
  topicItem: { display: 'flex', flexDirection: 'column', gap: 4, padding: 16, background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 10, cursor: 'pointer', textAlign: 'left', color: 'var(--text-primary)', fontSize: 14, transition: 'border-color 0.2s' },
  modalInput: { width: '100%', padding: '10px 12px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 14, outline: 'none' },
  cancelBtn: { padding: '8px 16px', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', cursor: 'pointer' },
  saveBtn: { padding: '8px 16px', background: 'var(--accent)', border: 'none', borderRadius: 8, color: 'white', fontWeight: 600, cursor: 'pointer' }
};
